import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import { QUESTION_INPUT_TYPE, QUESTION_TYPE_OPTIONS, QUESTIONS_TEMPLATES } from '../../../../../types/question.type';
import NumberRangeType from './components/NumberRangeType';
import LimitedAnswersOptions from './components/LimitedAnswersOptions';
import NumberRangeWithUnlimitedAnswer from './components/NumberRangeWithUnlimitedAnswer';
import QuestionErrorList from './components/QuestionErrorList';
import TimeTabConfiguration from './components/TimeTabConfiguration';
import DateAnalysisConfiguration from './components/DateAnalysisConfiguration';
import { validateQuestionValues } from '../../../../../utilis/common';
import { checkDuplicateQuestionLabel } from '../../../../../services/api/data-input.api';
import _ from 'lodash';
import { Box, TextareaAutosize, FormGroup, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, InputAdornment, Switch } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccountQuestions from './AccountQuestions';
import TemplateInfoDialog from './components/TemplateInfoDialog';
import ArchiveUpdateConfirmDialog from './components/ArchiveUpdateConfirmDialog';
import ChangeQuestionTypeConfirmDialog from './components/ChangeQuestionTypeConfirmDialog';
import WebIcon from '@mui/icons-material/Web';
import AlertDialog from '../../../../shared/AlertDialog';
import axios from '../../../../../axios';
import aiService from '../../../../../services/api/ai.service';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import { ADT_SUB_TYPES, COMMON_TRANSFER_ACCESSOR } from '../../../../../types/common.type';
import { AUTH_ROLES } from '../../../../../types/auth.type';
import { useSelector } from 'react-redux';
import { getAllUsersByAccount } from '../../../../../services/user.service';
import { getQuestionShares } from '../../../../../services/custom-tab-sharing.service';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
}));

const maskEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (!domain) return email;
    if (localPart.length <= 2) {
        return `${localPart[0]}***@${domain}`;
    } else if (localPart.length <= 4) {
        return `${localPart.substring(0, 2)}***@${domain}`;
    }
    return `${localPart.substring(0, 3)}***@${domain}`;
};

const AddQuestionDialog = ({
    open,
    onClose,
    onSave,
    editQuestion,
    loading,
    setEditQuestion,
    setQuestionValues,
    questionValues,
    setConfirmDialog,
    resetQuestionValues,
    isCustomQuestion,
    latestType,
    transferType,
    refreshQuestions,
    setIsArchivedDialog,
    currentQuestions,
    timeFieldQuestions,
    isFromArchiveList = false
}) => {
    const { common, auth } = useSelector((state) => ({
        common: state.common,
        auth: state.auth,
        permission: state.permission
    }));
    const [infoOpen, setInfoOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [accountQuestions, setAccountQuestions] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isVerifyingAI, setIsVerifyingAI] = useState(false);
    const [aiDescriptionError, setAiDescriptionError] = useState(null);
    const [isSharedConfirmOpen, setIsSharedConfirmOpen] = useState(false);
    const [shareUsers, setShareUsers] = useState([]);
    const [selectedShareUserIds, setSelectedShareUserIds] = useState([]);
    const [shareLoading, setShareLoading] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showArchiveUpdateConfirm, setShowArchiveUpdateConfirm] = useState(false);
    const [isUniversal, setIsUniversal] = useState(true);
    const [changeTypeConfirmConfig, setChangeTypeConfirmConfig] = useState({ open: false, onConfirm: null });

    const isArchiveEdit = useMemo(() => {
        return editQuestion?.isArchived && !editQuestion?.questionRefId ? true : false
    }, [editQuestion]);

    const handleClose = () => {
        resetQuestionValues();
        setEditQuestion(undefined);
        setAccountQuestions(false);
        setAiDescriptionError(null);
        setSelectedShareUserIds([]);
        setShareUsers([]);
        setUserSearchTerm('');
        setShowArchiveUpdateConfirm(false);
        setIsUniversal(true);
        setChangeTypeConfirmConfig({ open: false, onConfirm: null });
        onClose();
    };

    // Load account users for sharing when custom question dialog opens
    useEffect(() => {
        const loadShareUsers = async () => {
            if (!open || !isCustomQuestion || isFromArchiveList) return;
            setShareLoading(true);
            try {
                const users = await getAllUsersByAccount({ itemType: "CUSTOM_QUESTION" });
                setShareUsers(users || []);

                // If editing, fetch existing shares; otherwise default to all with universal access
                let selectedIds = [];
                let universal = !editQuestion?._id;
                if (editQuestion?._id) {
                    universal = false;
                    try {
                        const sharesResp = await getQuestionShares(editQuestion._id);
                        const shares = Array.isArray(sharesResp?.data?.shares) ? sharesResp.data.shares : Array.isArray(sharesResp) ? sharesResp : [];
                        const shareUserIds = shares
                            .map(s => {
                                if (s?.recipientId?._id) return s.recipientId._id;
                                if (typeof s?.recipientId === 'string') return s.recipientId;
                                if (s?.userId?._id) return s.userId._id;
                                if (typeof s?.userId === 'string') return s.userId;
                                if (s?.recipient?._id) return s.recipient._id;
                                return null;
                            })
                            .filter(Boolean);
                        selectedIds = shareUserIds;
                        // Set universal access state if applicable
                        if (sharesResp.data?.question?.isUniversal || sharesResp.data?.item?.isUniversal) {
                            universal = true;
                        }
                    } catch (err) {
                        console.error("Failed to load existing question shares", err);
                    }
                }
                // Check if the item itself is universal already
                if (editQuestion?.isUniversal) {
                    universal = true;
                }
                // For new questions, default to all users; for editing with no shares, leave empty
                if (!selectedIds.length && !editQuestion?._id) {
                    selectedIds = (users || []).map(u => u?._id).filter(Boolean);
                }
                setIsUniversal(universal);
                setSelectedShareUserIds(selectedIds);
                setQuestionValues((qv) => ({ ...qv, shareUserIds: selectedIds, isUniversal: universal }));
            } catch (error) {
                console.error("Failed to load users for sharing", error);
            } finally {
                setShareLoading(false);
            }
        };
        loadShareUsers();
    }, [open, isCustomQuestion, common?.selectedAccount?._id, setQuestionValues, editQuestion?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDuplicate = async (questionValues) => {
        let transferTypeArr = [transferType];
        if (transferType === "SNF" || transferType === "AMA" || transferType === "safeDischarge") {
            transferTypeArr = ["SNF"]
        }

        let updatedQ = await checkDuplicateQuestionLabel(
            {
                label: questionValues.label,
                _id: editQuestion?._id || null,
                tableLabel: questionValues.tableLabel,
                forType: latestType,
                ...latestType === "transfer" && { forTransferType: transferTypeArr }
            }
        );
        return updatedQ;
    }

    const shouldConfirmSharedQuestionUpdate = () => {
        return (
            latestType === "transfer" && (
                (COMMON_TRANSFER_ACCESSOR.includes(editQuestion?.accessor) &&
                    [ADT_SUB_TYPES.SNF, ADT_SUB_TYPES.SAFE_DISCHARGE, ADT_SUB_TYPES.AMA].includes(transferType)) ||
                (transferType === ADT_SUB_TYPES.TOTAL_COMMUNITY || editQuestion?.forTransferType === ADT_SUB_TYPES.TOTAL_COMMUNITY)
            )
        );
    };

    const handleSave = async ({ bypassSharedConfirm = false } = {}) => {
        const requiresConfirmation = shouldConfirmSharedQuestionUpdate();

        if (requiresConfirmation && !bypassSharedConfirm) {
            setIsSharedConfirmOpen(true);
            return;
        }
        // return false;
        let validationErrors = {};

        // Validate label for non-custom questions
        if (!isCustomQuestion && !questionValues.label) {
            validationErrors.label = 'Label is required.';
        }
        if (questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && !questionValues.timeRangeType) {
            validationErrors.timeRangeType = 'Time Range Type is required.';
        }

        // Validate custom questions
        if (isCustomQuestion) {
            validationErrors = validateQuestionValues(questionValues) || {};
        }

        // Proceed only if no validation errors
        if (_.isEmpty(validationErrors)) {
            const res = await handleDuplicate(questionValues);

            if (res) {
                const { label, tableLabel } = questionValues;

                // Check for duplicates
                if (res.label?.trim() === label?.trim()) {
                    validationErrors.label = 'Question Label already taken, please use another label.';
                }
                if (res.tableLabel?.trim() === tableLabel?.trim()) {
                    validationErrors.tableLabel = 'Table Question Label already taken, please use another label.';
                }
            }
        }

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setOpenErrorDialog(true); // Open the dialog if there are errors
            return;
        }
        if (editQuestion?._id) {
            if (editQuestion?.isArchived || questionValues?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS) {
                if (isArchiveEdit) {
                    if (editQuestion?.customQuestionInputType !== questionValues?.customQuestionInputType) {
                        setChangeTypeConfirmConfig({
                            open: true,
                            onConfirm: () => {
                                setChangeTypeConfirmConfig({ open: false, onConfirm: null });
                                onSave(isCustomQuestion);
                            }
                        });
                    } else {
                        setShowArchiveUpdateConfirm(true);
                    }
                } else {
                    if (editQuestion?.customQuestionInputType !== questionValues?.customQuestionInputType) {
                        setChangeTypeConfirmConfig({
                            open: true,
                            onConfirm: () => {
                                setChangeTypeConfirmConfig({ open: false, onConfirm: null });
                                onSave(isCustomQuestion);
                            }
                        });
                    } else {
                        onSave(isCustomQuestion);
                    }
                }
            } else {
                if (!questionValues?.customQuestionInputType) {
                    onSave(isCustomQuestion);
                } else {
                    if (editQuestion?.customQuestionInputType !== questionValues?.customQuestionInputType) {
                        setChangeTypeConfirmConfig({
                            open: true,
                            onConfirm: () => {
                                setChangeTypeConfirmConfig({ open: false, onConfirm: null });
                                setIsArchivedDialog(true);
                            }
                        });
                    } else {
                        setIsArchivedDialog(true);
                    }
                }
            }
        } else {
            setConfirmDialog(true);
        }
    };

    const handleAddOption = () => {
        setQuestionValues(prevValues => ({
            ...prevValues,
            customQuestionOptions: [...prevValues.customQuestionOptions, ''],
        }));
    };

    const handleRemoveOption = (index) => {
        setQuestionValues(prevValues => ({
            ...prevValues,
            customQuestionOptions: prevValues.customQuestionOptions.filter((_, i) => i !== index),
        }));
    };

    const handleEditOption = (index, value) => {
        setQuestionValues(prevValues => ({
            ...prevValues,
            customQuestionOptions: prevValues.customQuestionOptions.map((option, i) =>
                i === index ? value : option
            ),
        }));
    };

    const handleToggleShareUser = (userId) => {
        setSelectedShareUserIds((prev) => {
            const exists = prev.includes(userId);
            const updated = exists ? prev.filter(id => id !== userId) : [...prev, userId];
            setQuestionValues((qv) => ({ ...qv, shareUserIds: updated }));
            return updated;
        });
    };

    const handleUniversalChange = (checked) => {
        setIsUniversal(checked);
        if (checked) {
            const allUserIds = shareUsers.map(u => u?._id).filter(Boolean);
            setSelectedShareUserIds(allUserIds);
            setQuestionValues((qv) => ({ ...qv, shareUserIds: allUserIds, isUniversal: true }));
        } else {
            setSelectedShareUserIds([]);
            setQuestionValues((qv) => ({ ...qv, shareUserIds: [], isUniversal: false }));
        }
    };

    const handleToggleSelectAllShareUsers = (filteredUsers) => {
        const usersToToggle = filteredUsers || shareUsers;
        const filteredIds = usersToToggle.map(u => u?._id).filter(Boolean);
        const allFilteredSelected = filteredIds.every(id => selectedShareUserIds.includes(id));

        if (allFilteredSelected) {
            // Deselect all filtered users
            setSelectedShareUserIds((prev) => prev.filter(id => !filteredIds.includes(id)));
            setQuestionValues((qv) => ({
                ...qv,
                shareUserIds: qv.shareUserIds?.filter(id => !filteredIds.includes(id)) || []
            }));
        } else {
            // Select all filtered users
            setSelectedShareUserIds((prev) => {
                const newIds = filteredIds.filter(id => !prev.includes(id));
                return [...prev, ...newIds];
            });
            setQuestionValues((qv) => ({
                ...qv,
                shareUserIds: [...(qv.shareUserIds || []), ...filteredIds.filter(id => !qv.shareUserIds?.includes(id))]
            }));
        }
    };

    const handleInputChange = (field, value) => {
        setQuestionValues(prevValues => ({
            ...prevValues,
            [field]: value,
        }));
    };


    const handleCloseErrorDialog = () => {
        setOpenErrorDialog(false);
    };

    const handleDeleteQuestion = async () => {
        if (!editQuestion?._id) return;

        try {
            setIsDeleting(true);
            await axios.delete(`/api/question/set/?id=${editQuestion._id}${editQuestion?.isArchived ? '&archived=true' : ''}`);

            // Close the delete dialog and the main dialog
            setShowDeleteDialog(false);
            handleClose();

            // Refresh the questions list
            if (refreshQuestions) {
                refreshQuestions();
            }
        } catch (error) {
            console.error('Error deleting question:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleVerifyAIDescription = async () => {
        if (!questionValues.description || !questionValues.description.trim()) {
            return;
        }

        try {
            setIsVerifyingAI(true);
            setAiDescriptionError(null);

            const result = await aiService.verifyAIDescription(
                questionValues.description,
                questionValues.label,
                questionValues.customQuestionInputType
            );

            // Save the AI description to questionValues (using aiUnderstanding from backend response)
            handleInputChange('aiDescription', result.aiUnderstanding);

        } catch (error) {
            console.error('Error verifying AI description:', error);
            setAiDescriptionError(error.message || 'Failed to verify AI description');
        } finally {
            setIsVerifyingAI(false);
        }
    };

    const isDefaultCard = useMemo(() => {
        return editQuestion?.isDefaultCard || false;
    }, [editQuestion?.isDefaultCard]);

    // Filter users based on search term
    const filteredShareUsers = useMemo(() => {
        if (!userSearchTerm.trim()) {
            return shareUsers;
        }
        const searchLower = userSearchTerm.toLowerCase().trim();
        return shareUsers.filter((u) => {
            const fullName = `${u?.fullName || ''} ${u?.lastName || ''}`.trim().toLowerCase();
            const email = (u?.email || '').toLowerCase();
            const username = (u?.username || '').toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower) || username.includes(searchLower);
        });
    }, [shareUsers, userSearchTerm]);

    return (
        <>
            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <AlertDialog
                    content={`Delete "${questionValues?.label || editQuestion?.label}"? This will permanently remove this date analysis question.`}
                    handleClose={() => setShowDeleteDialog(false)}
                    withYesNo={true}
                    handleSubmit={handleDeleteQuestion}
                    loading={isDeleting}
                />
            )}

            {isSharedConfirmOpen && (
                <AlertDialog
                    content={`This change will update the shared question across Safe Discharge, SNF, and AMA transfers. Do you want to continue?`}
                    handleClose={() => setIsSharedConfirmOpen(false)}
                    withYesNo={true}
                    handleSubmit={() => {
                        setIsSharedConfirmOpen(false);
                        handleSave({ bypassSharedConfirm: true });
                    }}
                />
            )}

            {showArchiveUpdateConfirm && (
                <ArchiveUpdateConfirmDialog
                    open={showArchiveUpdateConfirm}
                    onClose={() => setShowArchiveUpdateConfirm(false)}
                    onOnlyUpdate={() => {
                        setShowArchiveUpdateConfirm(false);
                        onSave(isCustomQuestion);
                    }}
                    onUpdateAll={() => {
                        setShowArchiveUpdateConfirm(false);
                        onSave(isCustomQuestion, undefined, { isArchiveEdit: true });
                    }}
                    content={isFromArchiveList ? "This changes will apply for all account where used this question." : undefined}
                    hideOnlyUpdate={isFromArchiveList}
                />
            )}

            {changeTypeConfirmConfig.open && (
                <ChangeQuestionTypeConfirmDialog
                    open={changeTypeConfirmConfig.open}
                    onClose={() => setChangeTypeConfirmConfig({ open: false, onConfirm: null })}
                    onConfirm={changeTypeConfirmConfig.onConfirm}
                />
            )}

            <QuestionErrorList
                errors={errors}
                openErrorDialog={openErrorDialog}
                handleCloseErrorDialog={handleCloseErrorDialog}
            />
            {!!infoOpen &&
                <TemplateInfoDialog
                    onClose={() => setInfoOpen(false)}
                    open={infoOpen}
                    templateType={questionValues?.templateType}
                    handleSelectTemplate={(value) => handleInputChange('templateType', value)}
                />
            }
            {!!accountQuestions &&
                <AccountQuestions
                    handleClose={() => {
                        refreshQuestions();
                        handleClose();
                    }}
                    onClose={() => setAccountQuestions(false)}
                    latestType={latestType}
                    transferType={transferType}
                />
            }

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <StyledDialogTitle>
                    {editQuestion?._id ? 'Edit Question' : 'Add Question'}
                    <Box display="flex" alignItems="center" gap={1}>
                        <Button onClick={() => {
                            setAccountQuestions(true)
                        }}
                            variant='outlined' color='primary'>
                            Add Question From Archive
                        </Button>

                        {/* Show delete button only when editing a DATE_ANALYSIS question */}
                        {editQuestion?._id && (questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS || (questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && questionValues.existingAccessor)) && !isDefaultCard && (
                            <IconButton
                                aria-label="delete"
                                onClick={() => setShowDeleteDialog(true)}
                                sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                        backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                    }
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}

                        <IconButton aria-label="close" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </StyledDialogTitle>
                <DialogContent sx={{ padding: '20px' }}>
                    {!isCustomQuestion && (<>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Question Label"
                                variant="outlined"
                                fullWidth
                                value={questionValues.label}
                                onChange={(e) => handleInputChange('label', e.target.value)}
                            />
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Table Question Label"
                                variant="outlined"
                                fullWidth
                                value={questionValues.tableLabel}
                                onChange={(e) => handleInputChange('tableLabel', e.target.value)}
                            />
                        </FormControl>
                    </>
                    )}
                    {isCustomQuestion && (<>
                        {/* Share block - shown before template selection */}


                        {!isDefaultCard && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="question-type-label-id">Question Type</InputLabel>
                                <Select
                                    labelId="question-type-label-id"
                                    id="question-type"
                                    value={questionValues.customQuestionInputType}
                                    label="Question Type"
                                    onChange={(e) => {
                                        handleInputChange('customQuestionInputType', e.target.value)
                                        setQuestionValues(prevValues => ({
                                            ...prevValues,
                                            customQuestionOptions: [],
                                        }));
                                    }}
                                >
                                    {QUESTION_TYPE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && timeFieldQuestions.length > 0 && !isDefaultCard && (!editQuestion?._id || editQuestion?.existingAccessor) && (
                            <FormControl fullWidth margin="normal">
                                <InputLabel
                                    id="existing-time-field-label"
                                    shrink={true}
                                >
                                    Select Existing Time Field (Optional)
                                </InputLabel>
                                <Select
                                    labelId="existing-time-field-label"
                                    id="existing-time-field"
                                    value={questionValues.existingAccessor || ''}
                                    label="Select Existing Time Field (Optional)"
                                    onChange={(e) => {
                                        const val = e.target.value || null;
                                        handleInputChange('existingAccessor', val);
                                        if (val) {
                                            setQuestionValues(prev => ({
                                                ...prev,
                                                invisibleAnswerOptions: false,
                                                dataInputLabel: ''
                                            }));
                                        }
                                    }}
                                    displayEmpty
                                    disabled={!!editQuestion?._id && !!questionValues.existingAccessor}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {timeFieldQuestions.map((item) => (
                                        <MenuItem key={item?.question?.accessor} value={item?.question?.accessor}>
                                            {item?.question?.label || item?.question?.tableLabel || item?.question?.accessor}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Question Label"
                                variant="outlined"
                                fullWidth
                                value={questionValues.label}
                                onChange={(e) => handleInputChange('label', e.target.value)}
                            />
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <TextField
                                label="Table Question Label"
                                variant="outlined"
                                fullWidth
                                value={questionValues.tableLabel}
                                onChange={(e) => handleInputChange('tableLabel', e.target.value)}
                            />
                        </FormControl>

                        {questionValues.customQuestionInputType !== QUESTION_INPUT_TYPE.RESIDENCE_LIST && (
                            <>
                                <FormControl fullWidth margin="normal">
                                    <TextField
                                        label="Description"
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        fullWidth
                                        value={questionValues.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                height: "100%",
                                                borderRadius: "4px",
                                            },
                                        }}
                                        InputProps={{
                                            inputComponent: TextareaAutosize,
                                        }}
                                    />
                                </FormControl>

                                {questionValues.customQuestionInputType !== QUESTION_INPUT_TYPE.DATE_ANALYSIS && questionValues.customQuestionInputType !== QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST && (questionValues.customQuestionInputType !== QUESTION_INPUT_TYPE.TIME_TAB_RANGE || !questionValues.existingAccessor) && (
                                    <>
                                        <FormControl fullWidth margin="normal">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={questionValues?.invisibleAnswerOptions || false}
                                                        onChange={(e) => handleInputChange('invisibleAnswerOptions', e.target.checked)}
                                                    />
                                                }
                                                label="Enable hidden option"
                                            />
                                        </FormControl>

                                        {questionValues?.invisibleAnswerOptions && (
                                            <FormControl fullWidth margin="normal">
                                                <TextField
                                                    label="Label for data input"
                                                    variant="outlined"
                                                    fullWidth
                                                    value={questionValues?.dataInputLabel || ''}
                                                    onChange={(e) => handleInputChange('dataInputLabel', e.target.value)}
                                                />
                                            </FormControl>
                                        )}
                                    </>
                                )}

                                {/* Confirm with AI Description Button */}
                                {questionValues.description && questionValues.description.trim() && (
                                    <Box sx={{ mt: 1, mb: 2 }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={handleVerifyAIDescription}
                                            disabled={isVerifyingAI}
                                            startIcon={isVerifyingAI ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                        >
                                            {isVerifyingAI ? 'Verifying...' : 'Confirm with AI Description'}
                                        </Button>
                                    </Box>
                                )}

                                {/* Display AI Description */}
                                {questionValues.aiDescription && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            bgcolor: 'rgba(76, 175, 80, 0.08)',
                                            borderRadius: 1,
                                            border: '1px solid rgba(76, 175, 80, 0.3)'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                                            <strong>AI Description:</strong>
                                        </Box>
                                        <Box sx={{ pl: 4, color: 'text.secondary' }}>
                                            {questionValues.aiDescription}
                                        </Box>
                                    </Box>
                                )}

                                {/* Display Error */}
                                {aiDescriptionError && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            bgcolor: 'rgba(211, 47, 47, 0.08)',
                                            borderRadius: 1,
                                            border: '1px solid rgba(211, 47, 47, 0.3)',
                                            color: 'error.main'
                                        }}
                                    >
                                        {aiDescriptionError}
                                    </Box>
                                )}
                            </>
                        )}
                        {!isFromArchiveList && (
                            <Accordion sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, '&:before': { display: 'none' } }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        px: 2,
                                        py: 1.5,
                                        '&:hover': { bgcolor: '#fafafa' },
                                        '&.Mui-expanded': { bgcolor: '#fafafa' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <WebIcon fontSize="small" sx={{ color: '#4879f5' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                Share with users
                                            </Typography>
                                            {auth?.role?.slug !== AUTH_ROLES.USER && (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={isUniversal}
                                                            onChange={(e) => handleUniversalChange(e.target.checked)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: '#4879f5',
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: '#4879f5',
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <PublicIcon sx={{ color: '#4879f5', fontSize: 16 }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333333', fontSize: '0.75rem' }}>
                                                                Universal
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{ mb: 0, ml: 1 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                size="small"
                                                label={`${selectedShareUserIds.length} selected`}
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.75rem',
                                                    bgcolor: selectedShareUserIds.length ? 'rgba(72,121,245,0.08)' : '#f5f5f5',
                                                    color: selectedShareUserIds.length ? '#4879f5' : '#666',
                                                    border: `1px solid ${selectedShareUserIds.length ? 'rgba(72,121,245,0.4)' : 'rgba(0,0,0,0.12)'}`
                                                }}
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={filteredShareUsers.length > 0 && filteredShareUsers.every(u => selectedShareUserIds.includes(u?._id))}
                                                        onChange={() => handleToggleSelectAllShareUsers(filteredShareUsers)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" sx={{ color: '#555' }}>
                                                        Select all
                                                    </Typography>
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </Box>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#777', mb: 1, display: 'block' }}>
                                        Choose which users should see and use this custom question.
                                    </Typography>


                                    {shareLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={18} />
                                            <span style={{ fontSize: 14, color: '#666' }}>Loading users...</span>
                                        </Box>
                                    ) : (
                                        <>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Search users by name, email, or username..."
                                                value={userSearchTerm}
                                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                                sx={{ mb: 2 }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <Box sx={{ maxHeight: 260, overflowY: 'auto', pr: 1 }}>
                                                <FormGroup sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {filteredShareUsers.map((u) => {
                                                        const primary =
                                                            `${u?.fullName || ''} ${u?.lastName || ''}`.trim() ||
                                                            maskEmail(u?.email) ||
                                                            u?.username ||
                                                            'User';
                                                        const secondary =
                                                            u?.email && (u?.fullName || u?.lastName)
                                                                ? `(${maskEmail(u.email)})`
                                                                : '';
                                                        return (
                                                            <FormControlLabel
                                                                key={u?._id}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={selectedShareUserIds.includes(u?._id)}
                                                                        onChange={() => handleToggleShareUser(u?._id)}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                                                            {primary}
                                                                        </Typography>
                                                                        {secondary && (
                                                                            <Typography variant="caption" sx={{ color: '#777' }}>
                                                                                {secondary}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                }
                                                            />
                                                        );
                                                    })}
                                                    {filteredShareUsers.length === 0 && shareUsers.length > 0 && (
                                                        <span style={{ fontSize: 13, color: '#999' }}>No users found matching your search.</span>
                                                    )}
                                                    {shareUsers.length === 0 && (
                                                        <span style={{ fontSize: 13, color: '#999' }}>No users found for this account.</span>
                                                    )}
                                                </FormGroup>
                                            </Box>
                                        </>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.DATE && (
                            <FormControl fullWidth margin="normal">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={questionValues.useCustomCardInDashboard || false}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                handleInputChange('useCustomCardInDashboard', isChecked);
                                                // Automatically set template type to dayCard when checkbox is checked
                                                if (isChecked) {
                                                    handleInputChange('templateType', 'dayCard');
                                                }
                                            }}
                                            color="primary"
                                        />
                                    }
                                    label="Would you like to use custom card in dashboard by day wise?"
                                />
                            </FormControl>
                        )}

                        {(![QUESTION_INPUT_TYPE.RESIDENCE_LIST, QUESTION_INPUT_TYPE.DATE, QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST, QUESTION_INPUT_TYPE.TIME_TAB_RANGE].includes(questionValues.customQuestionInputType)) && !isDefaultCard && (
                            <FormControl fullWidth margin="normal">
                                <Box display="flex" alignItems="center">
                                    <Button
                                        onClick={() => setInfoOpen(true)}
                                        sx={{ ml: 1 }}
                                        variant="text"
                                        startIcon={<WebIcon />}
                                    >
                                        Choose Template ({QUESTIONS_TEMPLATES.find(template => template.value === questionValues.templateType)?.label})
                                    </Button>
                                </Box>
                            </FormControl>
                        )}

                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.LIMITED_ANSWERS && (
                            <LimitedAnswersOptions
                                options={questionValues?.customQuestionOptions}
                                onEdit={handleEditOption}
                                onRemove={handleRemoveOption}
                                onAdd={handleAddOption}
                            />
                        )}

                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE && (
                            <NumberRangeType
                                setQuestionValues={setQuestionValues}
                                questionValues={questionValues}
                            />
                        )}

                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS && (
                            <NumberRangeWithUnlimitedAnswer
                                setQuestionValues={setQuestionValues}
                                questionValues={questionValues}
                            />
                        )}

                        {/* {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST && (
                            <ResidentListOnlyInfo />
                        )} */}

                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && (
                            <TimeTabConfiguration
                                setQuestionValues={setQuestionValues}
                                questionValues={questionValues}
                                isDefaultCard={isDefaultCard}
                                editQuestion={editQuestion}
                            />
                        )}

                        {questionValues.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS && (
                            <DateAnalysisConfiguration
                                setQuestionValues={setQuestionValues}
                                questionValues={questionValues}
                                currentQuestions={currentQuestions}
                                isDefaultCard={isDefaultCard}
                            />
                        )}
                    </>)}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button onClick={handleClose} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={loading || shareLoading}>
                        {loading ? (
                            <>
                                <CircularProgress size={15} /> &nbsp; Saving...
                            </>
                        ) : (
                            'Save question'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddQuestionDialog;